import React, {useEffect, useRef, useState} from "react";
import "./ScrollingCross.css";
import gsap from "gsap";

const ArrowIcon = ({direction}) => (
  <svg
    className={`arrow-icon ${direction === "left" ? "flip" : ""}`}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
  >
    <path
      fill="currentColor"
      d="M13.025 1l-2.847 2.828 6.176 6.176H0v3.992h16.354l-6.176 6.176 2.847 2.828L24 12z"
    />
  </svg>
);

const ScrollingCross = ({topItems = [], bottomItems = []}) => {
  const trackTopRef = useRef(null);
  const trackBottomRef = useRef(null);
  const topTween = useRef(null);
  const bottomTween = useRef(null);
  const scrollDirRef = useRef("right");
  const lastScrollY = useRef(0);
  const [arrowDir, setArrowDir] = useState("right");

  const animateMarquee = (el, direction, tweenRef) => {
    if (!el) return;

    const distance = el.offsetWidth / 2; // Half of duplicated content

    if (tweenRef.current) {
      tweenRef.current.timeScale(direction === "left" ? 1 : -1);
      return;
    }

    tweenRef.current = gsap.to(el, {
      x: `-=${distance}`,
      duration: 250,
      ease: "linear",
      repeat: -1,
      modifiers: {
        x: gsap.utils.unitize((x) => parseFloat(x) % distance),
      },
    });

    if (direction === "right") {
      tweenRef.current.progress(1).timeScale(-1);
    } else {
      tweenRef.current.timeScale(1);
    }
  };

  useEffect(() => {
    // Ensure layout is painted before animating
    requestAnimationFrame(() => {
      animateMarquee(trackTopRef.current, "left", topTween);
      animateMarquee(trackBottomRef.current, "right", bottomTween);
    });

    const handleScroll = () => {
      const currentScroll = window.scrollY;
      const newDir = currentScroll > lastScrollY.current ? "right" : "left";

      if (newDir !== scrollDirRef.current) {
        scrollDirRef.current = newDir;
        setArrowDir(newDir);

        animateMarquee(
          trackTopRef.current,
          newDir === "right" ? "left" : "right",
          topTween
        );
        animateMarquee(trackBottomRef.current, newDir, bottomTween);
      }

      lastScrollY.current = currentScroll;
    };

    window.addEventListener("scroll", handleScroll, {passive: true});

    return () => {
      window.removeEventListener("scroll", handleScroll);
      topTween.current?.kill();
      bottomTween.current?.kill();
    };
  }, []);

  const renderRow = (items, reverse = false, ref, arrowDirection) => {
    const wrapperClass = reverse
      ? "scrolling-cross-reverse-wrapper"
      : "scrolling-cross-normal-wrapper";

    const trackClass = `marquee-text-track diagonal ${
      reverse ? "scrt-color-reverse reverse" : "scrt-color-normal"
    }`;

    const repeatedItems = [
      ...items,
      ...items,
      ...items,
      ...items,
      ...items,
      ...items,
    ]; // Only 2x to avoid layout overflow

    return (
      <div className={wrapperClass}>
        <div className={trackClass} ref={ref}>
          {repeatedItems.map((item, idx) => (
            <React.Fragment key={idx}>
              <p>{item}</p>
              {idx !== repeatedItems.length - 1 && (
                <span className="scrolling-cross-scrt-separator">
                  <ArrowIcon direction={arrowDirection} />
                </span>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="marquee-text">
      {renderRow(
        topItems,
        false,
        trackTopRef,
        arrowDir === "right" ? "left" : "right"
      )}
      {renderRow(bottomItems, true, trackBottomRef, arrowDir)}
    </div>
  );
};

export default ScrollingCross;
